import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  X,
  Loader2,
  ArrowRight,
  CheckCircle2,
  Package,
  User,
  Phone,
  Mail,
  Building2,
  Calendar,
  Receipt,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useQuoteDrawer } from "@/contexts/QuoteDrawerContext";

const CATEGORIES = [
  "Consumables",
  "Instruments",
  "Endodontics",
  "Orthodontics",
  "Prosthodontics",
  "Implantology",
  "Handpieces",
  "Dental Equipment",
  "Laboratory",
  "Basic Dental Products",
  "Other",
];

const TIMELINES = [
  { value: "urgent", label: "Urgent — within a week" },
  { value: "2_weeks", label: "1–2 weeks" },
  { value: "1_month", label: "Within a month" },
  { value: "quarter", label: "This quarter" },
  { value: "planning", label: "Just planning / no rush" },
];

const emptyForm = {
  name: "",
  clinic: "",
  phone: "",
  email: "",
  product: "",
  category: "",
  quantity: "",
  timeline: "2_weeks",
  gstin: "",
  notes: "",
};

/**
 * Push the payload to the API with an offline / mock-friendly fallback:
 * every submission is also mirrored to localStorage so the sales team can
 * export them from the browser if the API isn't reachable. Matches the
 * pattern already used in Staffing.jsx / ClinicSetup.jsx.
 */
async function submitQuote(payload) {
  try {
    await fetch("/api/quotes/requests", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
  } catch {
    /* offline / mock — we still persist locally below */
  }
  const key = "dyntra_quote_requests";
  const list = JSON.parse(localStorage.getItem(key) || "[]");
  list.push({ ...payload, submitted_at: new Date().toISOString() });
  localStorage.setItem(key, JSON.stringify(list));
}

export default function QuoteDrawer() {
  const { open, prefill, closeQuote } = useQuoteDrawer();
  const { user } = useAuth();
  const [form, setForm] = useState(emptyForm);
  const [status, setStatus] = useState("idle");
  const nameRef = useRef(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Reset + pre-fill whenever the drawer opens. We keep the customer's
  // contact details from the auth session if they're signed in so the form
  // only asks for the *requirement* half.
  useEffect(() => {
    if (!open) return;
    setStatus("idle");
    setForm({
      ...emptyForm,
      name: user?.name || user?.fullName || "",
      email: user?.email || "",
      phone: user?.phone || "",
      product: prefill?.product || "",
      category: prefill?.category || "",
      quantity: prefill?.quantity ? String(prefill.quantity) : "",
    });
    // Autofocus the first empty field on next paint.
    setTimeout(() => nameRef.current?.focus(), 100);
  }, [open, user, prefill]);

  // Escape to close + lock body scroll while drawer is open.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") closeQuote(); };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, closeQuote]);

  const submit = async (e) => {
    e.preventDefault();
    const name = form.name.trim();
    const phone = form.phone.trim();
    const product = form.product.trim();
    if (!name || !phone) {
      toast.error("Please enter your name and phone number so we can call you back.");
      return;
    }
    if (!product && !form.category) {
      toast.error("Tell us which product or category you need a quote for.");
      return;
    }
    if (!/^[0-9+\-\s]{7,}$/.test(phone)) {
      toast.error("That phone number doesn't look right — please double-check.");
      return;
    }
    setStatus("loading");
    try {
      await submitQuote({
        ...form,
        name,
        phone,
        product,
        source: "quote_drawer",
      });
      setStatus("success");
      toast.success("Quote request received — our sales team will get in touch within 4 hours.");
      setTimeout(() => {
        closeQuote();
      }, 1500);
    } catch {
      setStatus("idle");
      toast.error("Couldn't submit right now. Please try again.");
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        data-testid="quote-drawer-backdrop"
        onClick={closeQuote}
        aria-hidden={!open}
        className={`fixed inset-0 z-[90] bg-black/40 backdrop-blur-sm transition-opacity duration-300 ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      />

      {/* Drawer */}
      <aside
        role="dialog"
        aria-modal="true"
        aria-labelledby="quote-drawer-title"
        data-testid="quote-drawer"
        className={`fixed top-0 right-0 z-[100] h-full w-full sm:w-[460px] lg:w-[520px] bg-white shadow-2xl flex flex-col transition-transform duration-300 ease-out ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* Header */}
        <header className="relative shrink-0 bg-gradient-to-br from-[#361D82] via-[#4B2AAD] to-[#6B4CD9] text-white px-6 py-5">
          <button
            type="button"
            data-testid="quote-drawer-close"
            onClick={closeQuote}
            aria-label="Close quote form"
            className="absolute top-3 right-3 h-9 w-9 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.24em] text-white/75">
            <Package className="h-3.5 w-3.5" /> Bulk & Custom Pricing
          </div>
          <h2 id="quote-drawer-title" className="font-display text-2xl font-extrabold mt-1">Request a Quote</h2>
          <p className="text-sm text-white/85 mt-1">
            Share your requirement — our sales team will send a customised quote within 4 hours.
          </p>
        </header>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          <form id="quote-form" onSubmit={submit} className="space-y-5">
            <FieldGroup title="Your contact details">
              <Field
                label="Full name"
                required
                testid="quote-name"
                icon={User}
                value={form.name}
                onChange={(v) => set("name", v)}
                inputRef={nameRef}
                placeholder="Dr. Priya Sharma"
              />
              <Field
                label="Clinic / Company"
                testid="quote-clinic"
                icon={Building2}
                value={form.clinic}
                onChange={(v) => set("clinic", v)}
                placeholder="Sharma Dental Care"
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field
                  label="Phone"
                  required
                  testid="quote-phone"
                  icon={Phone}
                  type="tel"
                  value={form.phone}
                  onChange={(v) => set("phone", v)}
                  placeholder="+91 98xxxxxx00"
                />
                <Field
                  label="Email"
                  testid="quote-email"
                  icon={Mail}
                  type="email"
                  value={form.email}
                  onChange={(v) => set("email", v)}
                  placeholder="you@clinic.com"
                />
              </div>
            </FieldGroup>

            <FieldGroup title="What do you need?">
              <Field
                label="Product / SKU"
                testid="quote-product"
                icon={Package}
                value={form.product}
                onChange={(v) => set("product", v)}
                placeholder="e.g. Dentsply ProTaper Gold Files (Assorted)"
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <SelectField
                  label="Category"
                  testid="quote-category"
                  value={form.category}
                  onChange={(v) => set("category", v)}
                  options={[{ value: "", label: "Select category…" }, ...CATEGORIES.map((c) => ({ value: c, label: c }))]}
                />
                <Field
                  label="Quantity"
                  testid="quote-quantity"
                  type="number"
                  min="1"
                  value={form.quantity}
                  onChange={(v) => set("quantity", v)}
                  placeholder="e.g. 100"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <SelectField
                  label="Timeline"
                  testid="quote-timeline"
                  value={form.timeline}
                  onChange={(v) => set("timeline", v)}
                  icon={Calendar}
                  options={TIMELINES}
                />
                <Field
                  label="GSTIN (optional)"
                  testid="quote-gstin"
                  icon={Receipt}
                  value={form.gstin}
                  onChange={(v) => set("gstin", v.toUpperCase())}
                  placeholder="For GST-billed orders"
                />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Additional notes</label>
                <textarea
                  data-testid="quote-notes"
                  rows={4}
                  value={form.notes}
                  onChange={(e) => set("notes", e.target.value)}
                  placeholder="Brand preferences, delivery city, expected order frequency, packaging needs, etc."
                  className="mt-1 w-full rounded-xl border border-gray-200 focus:border-[#4B2AAD] focus:ring-2 focus:ring-[#4B2AAD]/15 px-4 py-2.5 outline-none text-sm resize-none"
                />
              </div>
            </FieldGroup>

            <p className="text-[11px] text-gray-500 leading-relaxed">
              By submitting this form you agree to be contacted by Dyntra Store's sales team via phone,
              email, or WhatsApp regarding your requirement. We never share your details with third parties.
            </p>
          </form>
        </div>

        {/* Footer (sticky CTA) */}
        <footer className="shrink-0 border-t border-gray-100 bg-white px-6 py-4">
          {status === "success" ? (
            <div
              data-testid="quote-success"
              className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-50 text-emerald-700 font-semibold text-sm py-3 border border-emerald-200"
            >
              <CheckCircle2 className="h-5 w-5" /> Request received — closing…
            </div>
          ) : (
            <button
              type="submit"
              form="quote-form"
              data-testid="quote-submit"
              disabled={status === "loading"}
              className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-[#4B2AAD] hover:bg-[#361D82] disabled:bg-[#4B2AAD]/70 text-white text-base font-bold py-3.5 transition-colors active:scale-[0.99]"
            >
              {status === "loading" ? (
                <><Loader2 className="h-5 w-5 animate-spin" /> Sending…</>
              ) : (
                <>Submit Quote Request <ArrowRight className="h-5 w-5" /></>
              )}
            </button>
          )}
          <p className="mt-2 text-center text-[11px] text-gray-500">
            Response within <span className="font-semibold text-gray-700">4 hours</span> during business hours (Mon–Sat, 9 AM – 7 PM IST).
          </p>
        </footer>
      </aside>
    </>
  );
}

function FieldGroup({ title, children }) {
  return (
    <fieldset className="space-y-3">
      <legend className="text-xs font-bold uppercase tracking-[0.18em] text-[#4B2AAD]">{title}</legend>
      {children}
    </fieldset>
  );
}

function Field({ label, testid, icon: Icon, value, onChange, type = "text", placeholder, required, min, inputRef }) {
  return (
    <div>
      <label className="text-xs font-bold uppercase tracking-wider text-gray-500">
        {label}{required ? " *" : ""}
      </label>
      <div className="relative mt-1">
        {Icon && (
          <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-gray-400">
            <Icon className="h-4 w-4" />
          </span>
        )}
        <input
          ref={inputRef}
          data-testid={testid}
          type={type}
          value={value}
          min={min}
          required={required}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          className={`w-full rounded-xl border border-gray-200 focus:border-[#4B2AAD] focus:ring-2 focus:ring-[#4B2AAD]/15 ${
            Icon ? "pl-9 pr-3" : "px-3"
          } py-2.5 outline-none text-sm bg-white`}
        />
      </div>
    </div>
  );
}

function SelectField({ label, testid, value, onChange, options, icon: Icon }) {
  return (
    <div>
      <label className="text-xs font-bold uppercase tracking-wider text-gray-500">{label}</label>
      <div className="relative mt-1">
        {Icon && (
          <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-gray-400">
            <Icon className="h-4 w-4" />
          </span>
        )}
        <select
          data-testid={testid}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={`w-full rounded-xl border border-gray-200 focus:border-[#4B2AAD] focus:ring-2 focus:ring-[#4B2AAD]/15 ${
            Icon ? "pl-9 pr-3" : "px-3"
          } py-2.5 outline-none text-sm bg-white`}
        >
          {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>
    </div>
  );
}
